import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Input, Button, Spinner } from '../../components/ui';
import { templateApi, UserTemplate, CreateUserTemplateRequest } from '../../services/api';
import styles from './UserTemplatesPage.module.css';

// Alias for clarity
type CreateUserTemplateDTO = CreateUserTemplateRequest;

interface TemplateFormData {
  name: string;
  description: string;
  shortDescription: string;
  category: string;
  tags: string;
  systemInstructions: string;
  conversationStarters: string;
  icon: string;
}

const initialFormData: TemplateFormData = {
  name: '',
  description: '',
  shortDescription: '',
  category: 'business-analysis',
  tags: '',
  systemInstructions: '',
  conversationStarters: '',
  icon: '📋',
};

const categoryOptions = [
  { value: 'business-analysis', label: 'Business Analysis' },
  { value: 'product-management', label: 'Product Management' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'design', label: 'Design' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Customer Support' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'data', label: 'Data & Analytics' },
  { value: 'security', label: 'Security' },
  { value: 'devops', label: 'DevOps' },
  { value: 'qa', label: 'Quality Assurance' },
];

const iconOptions = ['📋', '📊', '📦', '⚙️', '🎨', '📣', '💼', '🎧', '👥', '💰', '🔄', '📈', '🔒', '🚀', '🧪', '💡', '🤖', '📝'];

export const UserTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<UserTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<UserTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateApi.getUserTemplates();
      setTemplates(response.data || []);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setFormData(initialFormData);
    setEditingTemplate(null);
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template: UserTemplate) => {
    setFormData({
      name: template.name,
      description: template.description,
      shortDescription: template.shortDescription || '',
      category: template.category,
      tags: template.tags.join(', '),
      systemInstructions: template.systemInstructions,
      conversationStarters: template.conversationStarters.join('\n'),
      icon: template.icon || '📋',
    });
    setEditingTemplate(template);
    setShowCreateModal(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await templateApi.deleteUserTemplate(templateId);
      setTemplates(templates.filter((t) => t.id !== templateId));
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const templateData: CreateUserTemplateDTO = {
        name: formData.name,
        description: formData.description,
        shortDescription: formData.shortDescription || undefined,
        category: formData.category,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        systemInstructions: formData.systemInstructions,
        conversationStarters: formData.conversationStarters.split('\n').map((s) => s.trim()).filter(Boolean),
        icon: formData.icon,
        capabilities: [],
        integrations: [],
      };

      if (editingTemplate) {
        const response = await templateApi.updateUserTemplate(editingTemplate.id, templateData);
        setTemplates(templates.map((t) => (t.id === editingTemplate.id ? response.data : t)));
      } else {
        const response = await templateApi.createUserTemplate(templateData);
        setTemplates([...templates, response.data]);
      }

      setShowCreateModal(false);
      setFormData(initialFormData);
      setEditingTemplate(null);
    } catch (err) {
      setError('Failed to save template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = async (template: UserTemplate) => {
    try {
      const response = await templateApi.cloneTemplate(template.id, {
        customName: `${template.name} - ${new Date().toLocaleDateString()}`,
        customDescription: template.description,
        customSystemInstructions: template.systemInstructions,
      });
      navigate(`/assistants/${response.data.assistant.id}`);
    } catch (err) {
      setError('Failed to create assistant from template');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className={styles.loading}>
          <Spinner size="md" label="Loading your templates..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>My Templates</h1>
          <p className={styles.subtitle}>
            Create and manage your own assistant templates. Use them to quickly create new assistants
            with predefined configurations.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={() => navigate('/marketplace')}>
            Browse Marketplace
          </Button>
          <Button variant="primary" onClick={handleCreateTemplate}>
            + Create Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📋</div>
          <h3>No templates yet</h3>
          <p>Create your first template to save time when creating new assistants.</p>
          <Button variant="primary" onClick={handleCreateTemplate}>
            Create Your First Template
          </Button>
        </div>
      ) : (
        <div className={styles.templatesGrid}>
          {templates.map((template) => (
            <div key={template.id} className={styles.templateCard}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>{template.icon || '📋'}</span>
                <div className={styles.cardInfo}>
                  <h3 className={styles.cardName}>{template.name}</h3>
                  <span className={styles.cardCategory}>
                    {categoryOptions.find((c) => c.value === template.category)?.label || template.category}
                  </span>
                </div>
              </div>

              <p className={styles.cardDescription}>
                {template.shortDescription || template.description.substring(0, 120)}
                {template.description.length > 120 ? '...' : ''}
              </p>

              <div className={styles.cardTags}>
                {template.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className={styles.cardTag}>{tag}</span>
                ))}
                {template.tags.length > 3 && (
                  <span className={styles.cardTagMore}>+{template.tags.length - 3}</span>
                )}
              </div>

              <div className={styles.cardMeta}>
                <span className={styles.cardDate}>
                  Updated {new Date(template.updatedAt).toLocaleDateString()}
                </span>
                {template.isPublic && (
                  <span className={styles.publicBadge}>Public</span>
                )}
              </div>

              <div className={styles.cardActions}>
                <Button variant="primary" size="sm" onClick={() => handleUseTemplate(template)}>
                  Use Template
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingTemplate ? 'Edit Template' : 'Create Template'}</h2>
              <button className={styles.closeBtn} onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.iconSelector}>
                  <label className={styles.label}>Icon</label>
                  <div className={styles.iconGrid}>
                    {iconOptions.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        className={`${styles.iconOption} ${formData.icon === icon ? styles.iconSelected : ''}`}
                        onClick={() => setFormData({ ...formData, icon })}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Requirements Analyst"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Short Description</label>
                <Input
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                  placeholder="Brief one-liner description"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Description *</label>
                <textarea
                  className={styles.textarea}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of what this template does"
                  rows={3}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Category</label>
                  <select
                    className={styles.select}
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Tags (comma separated)</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="requirements, user stories, agile"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>System Instructions *</label>
                <textarea
                  className={styles.textarea}
                  value={formData.systemInstructions}
                  onChange={(e) => setFormData({ ...formData, systemInstructions: e.target.value })}
                  placeholder="Enter the AI system instructions for this template"
                  rows={8}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Conversation Starters (one per line)</label>
                <textarea
                  className={styles.textarea}
                  value={formData.conversationStarters}
                  onChange={(e) => setFormData({ ...formData, conversationStarters: e.target.value })}
                  placeholder="What can you help me with?&#10;Generate user stories for...&#10;Review this requirement..."
                  rows={4}
                />
              </div>

              <div className={styles.formActions}>
                <Button variant="secondary" type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorToast}>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      </div>
    </AppLayout>
  );
};

export default UserTemplatesPage;
